/**
 * Checkout Mercado Pago (Argentina, plan Basic / canal): URL final al hosted checkout.
 * No pasa por `/api/create-payment-intent` (eso es solo Stripe PaymentIntent).
 *
 * **Brand guard (cross-brand contamination prevention):** los env vars
 * `VITE_MP_CHECKOUT_*_AR` son globales por proyecto Vercel; si alguien copia
 * la config de una brand vecina o queda un valor stale, los AR users de ESTE
 * brand terminan suscribiéndose en la cuenta MP de OTRO merchant y la plata
 * se va al bolsillo equivocado. El bug se observó 2026-06-07 cuando
 * `VITE_MP_CHECKOUT_VIP_AR` apuntaba a un preapproval_plan de otro merchant;
 * todos los AR de esta brand pagaban a la cuenta equivocada. Mismo patrón que
 * el `metadata.source === BRAND_SOURCE_KEY`
 * filter del webhook Stripe — acá la brand-key gobierna si el fast-path MP
 * está habilitado. Si la brand no está en el whitelist, devolvemos "" y el
 * checkout VIP AR se bloquea en cliente (no cae a Stripe USD).
 *
 * Para agregar una brand al whitelist: confirmar primero que (a) tiene su
 * propio merchant MP, (b) los preapproval_plan_ids configurados en los env
 * vars pertenecen a ese merchant, y (c) las membership_products de la DB de
 * esa brand tienen `mercadopago_plan_id` no-NULL.
 */

import { BRAND_SOURCE_KEY } from "./brandConfig";
import { readRuntimeBrandEnv } from "./runtimeBrandEnv";
import {
  CANONICAL_AR_VIP_MP_CHECKOUT_URL,
  normalizeMercadoPagoCheckoutInput,
} from "./mercadoPagoPreapproval";

/**
 * Guard de forma: una env MP debe ser un checkout de suscripción con
 * preapproval_plan_id. Si está seteada pero NO valida (paste de otro dominio,
 * URL sin plan_id, garbage), la tratamos como "no seteada" y el cascade sigue
 * — mejor caer a la siguiente env / ocultar el CTA que mandar al visitante AR
 * a una URL que no puede cobrar la suscripción mensual. Preserva la URL
 * original (con sus params) cuando es válida; no la reconstruye.
 */
function sanitizeMpCheckoutUrl(raw: string): string {
  const u = raw.trim();
  if (!u) return "";
  // Acepta plan_id pelado O URL completa → siempre URL canónica con preapproval_plan_id.
  const normalized = normalizeMercadoPagoCheckoutInput(u);
  if (normalized.valid && normalized.url) return normalized.url;
  if (typeof console !== "undefined") {
    console.warn(
      "[mercadopagoArCheckoutUrl] env MP checkout inválida (sin preapproval_plan_id o no-MP); ignorando:",
      u.slice(0, 80),
    );
  }
  return "";
}

/** Solo la brand actual (BRAND_SOURCE_KEY). Nunca hardcodear otra brand aquí. */
const BRANDS_WITH_OWN_MP_AR_MERCHANT: ReadonlySet<string> = new Set(
  BRAND_SOURCE_KEY ? [BRAND_SOURCE_KEY] : [],
);

function brandHasOwnMpMerchant(): boolean {
  return BRANDS_WITH_OWN_MP_AR_MERCHANT.has(BRAND_SOURCE_KEY);
}

function trimEnv(...candidates: (string | undefined)[]): string {
  for (const c of candidates) {
    const t = typeof c === "string" ? c.trim() : "";
    if (t) return t;
  }
  return "";
}

function runtimeEnv(name: string): string {
  return readRuntimeBrandEnv(name);
}

/** URL completa MP si está configurada; legacy `VITE_MP_TELEGRAM_GROUP_LINK` solo si apunta a mercadopago.com. */
export function getMercadoPagoCheckoutBasicArUrl(): string {
  if (!brandHasOwnMpMerchant()) return "";
  const primary = sanitizeMpCheckoutUrl(trimEnv(runtimeEnv("VITE_MP_CHECKOUT_BASIC_AR")));
  if (primary) return primary;
  const legacy = sanitizeMpCheckoutUrl(trimEnv(runtimeEnv("VITE_MP_TELEGRAM_GROUP_LINK")));
  if (legacy) return legacy;
  return "";
}

/**
 * URL completa MP para VIP Argentina (Comunidad / `/invitacion-premium`).
 * Prioridad: env `VITE_MP_CHECKOUT_VIP_AR` (plan_id o URL) → canónico $49.999 ARS.
 */
export function getMercadoPagoCheckoutVipArUrl(): string {
  if (!brandHasOwnMpMerchant()) return "";
  const fromEnv = sanitizeMpCheckoutUrl(trimEnv(runtimeEnv("VITE_MP_CHECKOUT_VIP_AR")));
  if (fromEnv) return fromEnv;
  // Fallback duro: plan VIP comunidad (no Basic, no legacy $19.999)
  return CANONICAL_AR_VIP_MP_CHECKOUT_URL;
}

/**
 * URL completa MP para VIP Argentina con descuento (env legacy).
 *
 * @deprecated Política 2026-07: descuento NO se aplica en MercadoPago.
 * Checkout VIP AR debe usar {@link getMercadoPagoCheckoutVipArUrl} (precio full).
 * El 50% primer mes es solo Stripe exterior (`promo_1Txbe…`).
 * Se mantiene para admin Comunidad / lectura de env; no usar en funnels de pago.
 */
export function getMercadoPagoCheckoutVipArDiscountUrl(): string {
  if (!brandHasOwnMpMerchant()) return "";
  return sanitizeMpCheckoutUrl(trimEnv(runtimeEnv("VITE_MP_CHECKOUT_VIP_DISCOUNT_AR")));
}

/**
 * Cascade del URL MP hosted preferido para el hero CTA "Ser Exclusivo" en AR.
 * Probamos en orden de prioridad y devolvemos la primera env var seteada:
 *   1. VITE_MP_CHECKOUT_BASIC_AR        (membresía mensual estándar)
 *   2. VITE_MP_CHECKOUT_VIP_AR          (VIP precio completo — sin descuento)
 *   3. legacy VITE_MP_TELEGRAM_GROUP_LINK si apunta a mercadopago.com
 *
 * Política: descuento/promo NO se aplica en MercadoPago (solo Stripe exterior).
 * Visitor AR SIEMPRE va a URL MP hosted con preapproval_plan_id del merchant.
 * Si NINGUNA env está seteada, devuelve "" y el caller oculta el CTA.
 */
export function getMercadoPagoArMembershipUrl(): string {
  if (!brandHasOwnMpMerchant()) return "";
  const basic = sanitizeMpCheckoutUrl(trimEnv(runtimeEnv("VITE_MP_CHECKOUT_BASIC_AR")));
  if (basic) return basic;
  const vip = sanitizeMpCheckoutUrl(trimEnv(runtimeEnv("VITE_MP_CHECKOUT_VIP_AR")));
  if (vip) return vip;
  const legacy = sanitizeMpCheckoutUrl(trimEnv(runtimeEnv("VITE_MP_TELEGRAM_GROUP_LINK")));
  if (legacy) return legacy;
  return "";
}
